import Stack from '@mui/material/Stack';

import type { DevelopmentDetailAiAnalysisResponse, DevelopmentDetailDataModel } from '@/domain/models/development.js';
import type { ProjectUserResponse } from '@/domain/models/project.js';
import type { ReasonModel } from '@/domain/models/reason.js';
import { EmptyState } from '@/presentation/components/index.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import type { EstimateFormState } from '@/presentation/hooks/use-estimate-form.js';
import { EstimateDecisionActions } from '@/presentation/pages/developments/development-detail/components/tabs/estimate-decision-actions.js';
import { EstimateForm } from '@/presentation/pages/developments/development-detail/components/tabs/estimate-form.js';
import { EstimateHighlightBox } from '@/presentation/pages/developments/development-detail/components/tabs/estimate-highlight-box.js';
import { EstimateHistoryAccordion } from '@/presentation/pages/developments/development-detail/components/tabs/estimate-history-accordion.js';
import { TechLeadAssignmentCard } from '@/presentation/pages/developments/development-detail/components/tabs/tech-lead-assignment-card.js';

interface Props {
    dev: DevelopmentDetailDataModel;
    showTechLeadCard: boolean;
    techLeads: ProjectUserResponse[];
    selectedTechLeadId: string;
    onSelectTechLead: (id: string) => void;
    assigningTechLead: boolean;
    assignTechLeadError: string | null;
    onAssignTechLead: () => void;
    hasTechLead: boolean;
    canViewSuggestedEstimates: boolean;
    latestAnalysis: DevelopmentDetailAiAnalysisResponse | null;
    showEstimateForm: boolean;
    canApproveEstimate: boolean;
    approvingEstimate: boolean;
    onRejectEstimate: () => void;
    onApproveEstimate: () => void;
    showFactoryEstimateForm: boolean;
    functionalEstimateIsComplete: boolean;
    estimateForm: EstimateFormState;
    onEstimateFieldChange: <K extends keyof EstimateFormState>(key: K, value: EstimateFormState[K]) => void;
    estimateIsSuperior: boolean;
    effortIncreaseReasons: ReasonModel[];
    submittingEstimate: boolean;
    onSubmitEstimate: () => void;
}

export function EstimateTab({
    dev,
    showTechLeadCard,
    techLeads,
    selectedTechLeadId,
    onSelectTechLead,
    assigningTechLead,
    assignTechLeadError,
    onAssignTechLead,
    hasTechLead,
    canViewSuggestedEstimates,
    latestAnalysis,
    showEstimateForm,
    canApproveEstimate,
    approvingEstimate,
    onRejectEstimate,
    onApproveEstimate,
    showFactoryEstimateForm,
    functionalEstimateIsComplete,
    estimateForm,
    onEstimateFieldChange,
    estimateIsSuperior,
    effortIncreaseReasons,
    submittingEstimate,
    onSubmitEstimate
}: Props) {
    const { translate } = useApp();

    return (
        <Stack spacing={2}>
            {showTechLeadCard && (
                <TechLeadAssignmentCard
                    techLeads={techLeads}
                    selectedTechLeadId={selectedTechLeadId}
                    currentTechLeadId={dev.liderTecnicoId}
                    onSelectTechLead={onSelectTechLead}
                    assigningTechLead={assigningTechLead}
                    assignTechLeadError={assignTechLeadError}
                    onAssign={onAssignTechLead}
                />
            )}
            {/* Quem tem o formulário de atribuição acima não precisa da mensagem — ela é para quem só pode aguardar. */}
            {!hasTechLead && !showTechLeadCard && (
                <EmptyState title={translate('estimate.awaitingTechLeadTitle')} message={translate('estimate.awaitingTechLeadMessage')} />
            )}
            {hasTechLead && (
                <>
                    <EstimateHighlightBox title={translate('estimate.functionalEstimate')} complexity={dev.functionalComplexity} estimatedHours={dev.functionalHours} />
                    {canViewSuggestedEstimates && latestAnalysis && (
                        <EstimateHighlightBox
                            title={translate('estimate.aiReferenceTitle')}
                            tone="primary"
                            complexity={latestAnalysis.suggestedComplexity}
                            estimatedHours={latestAnalysis.suggestedHours}
                            aiScore={latestAnalysis.score}
                        />
                    )}
                    {!showEstimateForm && dev.currentEstimate && (
                        <Stack spacing={2}>
                            <EstimateHighlightBox
                                title={`${translate('estimate.technicalEstimate')} v${dev.currentEstimate.version}`}
                                complexity={dev.currentEstimate.complexity}
                                estimatedHours={dev.currentEstimate.estimatedHours}
                                estimatedDeliveryDate={dev.currentEstimate.estimatedDeliveryDate}
                            />
                            {canApproveEstimate && !dev.currentEstimate.rejected && dev.status === 'AGUARDANDO_APROVACAO_ESTIMATIVA' && (
                                <EstimateDecisionActions
                                    rejectLabel={translate('estimate.reject')}
                                    approveLabel={translate('estimate.approve')}
                                    approvingLabel={translate('estimate.approving')}
                                    approving={approvingEstimate}
                                    onReject={onRejectEstimate}
                                    onApprove={onApproveEstimate}
                                />
                            )}
                        </Stack>
                    )}
                    {showFactoryEstimateForm && (
                        <EstimateForm
                            functionalEstimateIsComplete={functionalEstimateIsComplete}
                            form={estimateForm}
                            onFieldChange={onEstimateFieldChange}
                            isSuperior={estimateIsSuperior}
                            effortIncreaseReasons={effortIncreaseReasons}
                            submitting={submittingEstimate}
                            onSubmit={onSubmitEstimate}
                        />
                    )}
                    {dev.estimates.length > 0 && (
                        <EstimateHistoryAccordion
                            estimates={dev.estimates}
                            historyLabel={translate('estimate.estimateHistory')}
                            versionLabel={translate('estimate.version')}
                            rejectedLabel={translate('estimate.rejected')}
                        />
                    )}
                </>
            )}
        </Stack>
    );
}
